/**
 * LE BFR, CÔTÉ ÉCRAN — lot 99, 18/09/2026.
 *
 * Transforme la lecture de `GET /analyse/bfr` en entrées du calcul partagé,
 * puis en séries pour les huit courbes. Aucun dessin ici.
 */

import {
  bfrSemaine,
  centimes,
  delaiSemaine,
  fluxSemaine,
  margeSemaine,
  semainesDe,
  type BfrSemaine,
  type DelaiSemaine,
  type EntreesBfr,
  type Fenetre,
  type Filtre,
  type FluxSemaine,
  type LignePrevueBfr,
  type MargeSemaine,
  type OrdreBfr,
  type PieceBfr,
  type SemaineBfr,
} from '@stafa/core';

export interface ReglagesAnalyse {
  readonly jour_tva: number;
  readonly charges_sociales: string;
  readonly jour_charges_sociales: number;
  readonly delai_facture_affretement: number;
  readonly delai_paiement_affretement: number;
  readonly delai_client: number;
  readonly retenue_factor: string;
  readonly commission_factor: string;
  readonly delai_cession: number;
  readonly minimum_tresorerie: string;
  readonly decouvert_autorise: string;
  readonly modifie_par: string | null;
  readonly modifie_le: string | null;
}

export interface BfrLu {
  readonly aujourdhui: string;
  readonly debut_stafa: string;
  readonly activites: readonly { id: string; nom: string; actif: boolean }[];
  readonly reglages: ReglagesAnalyse;
  readonly clients: readonly PieceBfr[];
  readonly fournisseurs: readonly PieceBfr[];
  readonly ordres: readonly OrdreBfr[];
  readonly lignes: readonly LignePrevueBfr[];
  readonly tiersRemplaces: readonly string[];
  readonly prelevees: readonly string[];
  readonly comptes: readonly {
    id: string;
    nom: string;
    solde: string | null;
    lu_le: string;
  }[];
  readonly mouvements: readonly {
    compte: string | null;
    date: string;
    montant: string;
  }[];
}

export function entreesDe(l: BfrLu): EntreesBfr {
  return {
    aujourdhui: l.aujourdhui,
    debutStafa: l.debut_stafa,
    reglages: {
      jour_tva: l.reglages.jour_tva,
      charges_sociales: centimes(l.reglages.charges_sociales),
      jour_charges_sociales: l.reglages.jour_charges_sociales,
      delai_facture_affretement: l.reglages.delai_facture_affretement,
      delai_paiement_affretement: l.reglages.delai_paiement_affretement,
      delai_client: l.reglages.delai_client,
    },
    clients: l.clients,
    fournisseurs: l.fournisseurs,
    ordres: l.ordres,
    lignes: l.lignes,
    tiersRemplaces: new Set(l.tiersRemplaces),
  };
}

/** Les clients qu'on peut choisir : ceux des pièces et ceux des ordres. */
export function clientsDe(l: BfrLu): { cle: string; nom: string }[] {
  const carte = new Map<string, string>();
  for (const p of l.clients) carte.set(p.tiers, p.tiers_nom);
  for (const o of l.ordres) if (!carte.has(o.tiers)) carte.set(o.tiers, o.tiers_nom);
  return [...carte]
    .filter(([cle]) => cle !== 'sans-tiers')
    .map(([cle, nom]) => ({ cle, nom }))
    .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
}

/**
 * LES SOLDES À UNE DATE passée : le solde lu aujourd'hui, moins les
 * mouvements postérieurs. Nul pour un compte sans solde lisible, et pour une
 * date à venir — les soldes futurs sont l'objet de la trésorerie.
 */
export function soldesA(
  l: BfrLu,
  date: string,
): { nom: string; solde: number | null }[] {
  return l.comptes.map((c) => {
    if (c.solde === null || date > l.aujourdhui) return { nom: c.nom, solde: null };
    const apres = l.mouvements
      .filter((m) => m.compte === c.id && m.date > date)
      .reduce((s, m) => s + centimes(m.montant), 0);
    return { nom: c.nom, solde: centimes(c.solde) - apres };
  });
}

export interface Tableau {
  readonly semaines: readonly SemaineBfr[];
  readonly bfr: readonly BfrSemaine[];
  readonly clients: readonly FluxSemaine[];
  readonly fournisseurs: readonly FluxSemaine[];
  readonly dso: readonly DelaiSemaine[];
  readonly dpo: readonly DelaiSemaine[];
  readonly marge: readonly MargeSemaine[];
  readonly courant: number;
  readonly futurDepuis: number;
}

export function tableau(e: EntreesBfr, f: Filtre, fenetre: Fenetre): Tableau {
  const semaines = semainesDe(e.aujourdhui, fenetre);
  const bfr = semaines.map((s) => bfrSemaine(e, f, s));
  const courant = semaines.findIndex((s) => s.courante);
  const futur = semaines.findIndex((s) => s.future);
  return {
    semaines,
    bfr,
    clients: semaines.map((s) => fluxSemaine(e, f, s, 'client')),
    fournisseurs: semaines.map((s) => fluxSemaine(e, f, s, 'fournisseur')),
    dso: semaines.map((s, i) => delaiSemaine(e, f, s, 'client', bfr[i] as BfrSemaine)),
    dpo: semaines.map((s, i) =>
      delaiSemaine(e, f, s, 'fournisseur', bfr[i] as BfrSemaine),
    ),
    marge: semaines.map((s) => margeSemaine(e, f, s)),
    courant,
    futurDepuis: futur === -1 ? semaines.length : futur,
  };
}

const MILLIERS = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

/** « 12,5 k€ », « −3 k€ », « 850 € » — pour les axes et les étiquettes. */
export function eurosCourts(c: number): string {
  const e = c / 100;
  if (Math.abs(e) >= 1000) {
    return `${MILLIERS.format(Math.round(e / 100) / 10)} k€`.replace('-', '−');
  }
  return `${String(Math.round(e))} €`.replace('-', '−');
}

export function euros(c: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
    c / 100,
  );
}

export function jours(v: number): string {
  return `${String(Math.round(v))} j`;
}

export function dateFr(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

export function dateCourte(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

export const FENETRES: readonly { cle: Fenetre; nom: string }[] = [
  { cle: 'centre', nom: '6 passées + 6 à venir' },
  { cle: 'passe', nom: '12 passées' },
  { cle: 'avenir', nom: '12 à venir' },
];
